/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Collapse,
  Divider,
  Chip,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Link as LinkIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface CreatorInviteLinksProps {
  debateId: string;
  teamId: string;
  teamName: string;
  teamSize: number;
  opponentTeamName: string;
  opponentTeamSize: number;
  isCreator: boolean;
  // Pass the existing invite links from debate data
  opponentInviteLink?: string;
  teamInviteCode?: string;
}

const CreatorInviteLinks: React.FC<CreatorInviteLinksProps> = ({
  debateId,
  teamId,
  teamName,
  teamSize,
  opponentTeamName,
  opponentTeamSize,
  isCreator,
  opponentInviteLink,
  teamInviteCode
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Don't render if not creator
  if (!isCreator) return null;

  // Generate team invite link from team invite code
  const teamInviteLink = teamInviteCode 
    ? `${window.location.origin}/invite/team/${teamInviteCode}`
    : '';

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleCopyLink = (link: string, type: string) => {
    navigator.clipboard.writeText(link);
    toast.success(`${type} link copied!`);
  };

  // Don't show if no invite links are available
  if (!opponentInviteLink && !teamInviteLink) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={handleToggleExpanded}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        startIcon={<LinkIcon />}
        fullWidth
        sx={{ 
          justifyContent: 'space-between',
          textTransform: 'none',
          borderColor: '#1976d2',
          color: '#1976d2'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            📧 {t('inviteLinks')}
          </Typography>
          <Chip 
            label={t('creatorLabel')} 
            size="small" 
            color="primary" 
            sx={{ fontSize: '0.6rem', height: 18 }}
          />
        </Box>
      </Button>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9ff', borderRadius: 1, border: '1px solid #e3f2fd' }}>
          {/* Opponent Invite Link */}
          {opponentInviteLink && (
            <Box sx={{ mb: teamInviteLink ? 2 : 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon fontSize="small" />
                {opponentTeamSize > 1 ? t('inviteOpponents') : t('inviteOpponent')} ({opponentTeamName})
              </Typography>
              
              <TextField
                fullWidth
                size="small"
                value={opponentInviteLink}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('copyOpponentInviteLink')}>
                        <IconButton 
                          onClick={() => handleCopyLink(opponentInviteLink, 'Opponent')}
                          edge="end"
                          size="small"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: '0.8rem',
                    fontFamily: 'monospace'
                  }
                }}
              />
            </Box>
          )}

          {/* Team Invite Link (only if team size > 1 and team invite code exists) */}
          {teamInviteLink && teamSize > 1 && (
            <>
              {opponentInviteLink && <Divider sx={{ my: 1 }} />}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon fontSize="small" />
                  {t('inviteTeamMembers')} ({teamName})
                </Typography>
                
                <TextField
                  fullWidth
                  size="small"
                  value={teamInviteLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t('copyTeamInviteLink')}>
                          <IconButton 
                            onClick={() => handleCopyLink(teamInviteLink, 'Team')}
                            edge="end"
                            size="small"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      fontSize: '0.8rem',
                      fontFamily: 'monospace'
                    }
                  }}
                />
              </Box>
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('inviteLinksHelpText')}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CreatorInviteLinks; 